// =============================================================================
// VendorSettingsModal — edit a vendor's config (billing shape, budgets,
// balance, alert threshold, etc.) so the Runway board can be tuned without a
// migration. Writes via updateVendorAccount (UPDATE only — the row exists).
// =============================================================================

import { useState } from 'react';
import { toast } from 'sonner';
import { Modal } from '@/components/shared/UIComponents';
import { updateVendorAccount, type VendorAccount } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';

const SHAPES: VendorAccount['billing_shape'][] = ['metered', 'prepaid', 'subscription', 'sandbox', 'build'];
const centsToStr = (c: number | null | undefined) => (c == null ? '' : String(c / 100));
const dollarsToCents = (s: string) => (s.trim() === '' ? null : Math.round(parseFloat(s) * 100));
const numOrNull = (s: string) => (s.trim() === '' ? null : Number(s));

export function VendorSettingsModal({ vendor, onClose, onSaved }: {
  vendor: VendorAccount; onClose: () => void; onSaved: () => void;
}) {
  const [shape, setShape] = useState<VendorAccount['billing_shape']>(vendor.billing_shape);
  const [f, setF] = useState({
    display_name:   vendor.display_name,
    category:       vendor.category,
    monthly_budget: centsToStr(vendor.monthly_budget_cents),
    monthly_amount: centsToStr(vendor.monthly_amount_cents),
    balance:        centsToStr(vendor.balance_cents),
    credits:        vendor.credits_balance != null ? String(vendor.credits_balance) : '',
    credit_unit:    vendor.credit_unit ?? '',
    renewal_day:    vendor.renewal_day != null ? String(vendor.renewal_day) : '',
    alert_days:     String(vendor.low_runway_alert_days ?? 7),
    attributable:   vendor.is_tenant_attributable,
    active:         vendor.is_active,
    notes:          vendor.notes ?? '',
  });
  const [saving, setSaving] = useState(false);
  const upd = (k: keyof typeof f, v: string | boolean) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await updateVendorAccount(vendor.vendor_key, {
        display_name:           f.display_name.trim() || vendor.display_name,
        category:               f.category.trim() || 'other',
        billing_shape:          shape,
        monthly_budget_cents:   dollarsToCents(f.monthly_budget),
        monthly_amount_cents:   dollarsToCents(f.monthly_amount),
        balance_cents:          dollarsToCents(f.balance),
        credits_balance:        numOrNull(f.credits),
        credit_unit:            f.credit_unit.trim() || null,
        renewal_day:            numOrNull(f.renewal_day),
        low_runway_alert_days:  Number(f.alert_days) || 7,
        is_tenant_attributable: f.attributable,
        is_active:              f.active,
        notes:                  f.notes.trim() || null,
      });
      toast.success('Vendor updated');
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e) || 'Failed to update vendor');
    } finally {
      setSaving(false);
    }
  };

  const field = 'input-field mt-1 w-full';
  const lbl = 'block text-sm';
  const span = 'text-surface-600';

  return (
    <Modal open onClose={onClose} title={`Vendor settings — ${vendor.display_name}`} subtitle={vendor.vendor_key}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className={lbl}><span className={span}>Display name</span>
            <input className={field} value={f.display_name} onChange={e => upd('display_name', e.target.value)} /></label>
          <label className={lbl}><span className={span}>Category</span>
            <input className={field} value={f.category} onChange={e => upd('category', e.target.value)} placeholder="llm / image / maps / infra…" /></label>
        </div>

        <label className={lbl}><span className={span}>Billing shape</span>
          <select className={field} value={shape} onChange={e => setShape(e.target.value as VendorAccount['billing_shape'])}>
            {SHAPES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <span className="text-[11px] text-surface-400">
            {shape === 'metered' && 'Pay-per-use — tracked spend vs a monthly budget/cap.'}
            {shape === 'prepaid' && 'Buy a balance up front; usage drains it (runway = balance ÷ burn).'}
            {shape === 'subscription' && 'Flat recurring — renews monthly on a set day.'}
            {shape === 'build' && 'R&D / build tooling — shown but excluded from cost-to-serve.'}
            {shape === 'sandbox' && 'Not live yet.'}
          </span>
        </label>

        {shape === 'metered' && (
          <label className={lbl}><span className={span}>Monthly budget / cap (USD)</span>
            <input type="number" step="0.01" className={field} value={f.monthly_budget} onChange={e => upd('monthly_budget', e.target.value)} placeholder="54.00" /></label>
        )}
        {shape === 'prepaid' && (
          <div className="grid grid-cols-3 gap-3">
            <label className={lbl}><span className={span}>Balance (USD)</span>
              <input type="number" step="0.01" className={field} value={f.balance} onChange={e => upd('balance', e.target.value)} /></label>
            <label className={lbl}><span className={span}>Credits left</span>
              <input type="number" className={field} value={f.credits} onChange={e => upd('credits', e.target.value)} /></label>
            <label className={lbl}><span className={span}>Credit unit</span>
              <input className={field} value={f.credit_unit} onChange={e => upd('credit_unit', e.target.value)} placeholder="credit" /></label>
          </div>
        )}
        {(shape === 'subscription' || shape === 'build') && (
          <div className="grid grid-cols-2 gap-3">
            <label className={lbl}><span className={span}>Monthly amount (USD)</span>
              <input type="number" step="0.01" className={field} value={f.monthly_amount} onChange={e => upd('monthly_amount', e.target.value)} placeholder="54.00" /></label>
            <label className={lbl}><span className={span}>Renews on day (1–31)</span>
              <input type="number" min="1" max="31" className={field} value={f.renewal_day} onChange={e => upd('renewal_day', e.target.value)} /></label>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 items-end">
          <label className={lbl}><span className={span}>Low-runway alert (days)</span>
            <input type="number" className={field} value={f.alert_days} onChange={e => upd('alert_days', e.target.value)} /></label>
          <div className="flex flex-col gap-1.5 text-sm pb-1">
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={f.attributable} onChange={e => upd('attributable', e.target.checked)} /> <span className={span}>Per-tenant attributable</span></label>
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={f.active} onChange={e => upd('active', e.target.checked)} /> <span className={span}>Active (show on board)</span></label>
          </div>
        </div>

        <label className={lbl}><span className={span}>Notes</span>
          <input className={field} value={f.notes} onChange={e => upd('notes', e.target.value)} /></label>

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm text-surface-600 hover:bg-surface-100">Cancel</button>
          <button onClick={save} disabled={saving} className="px-3 py-1.5 rounded-lg text-sm bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </Modal>
  );
}
